import * as z from 'zod/mini';
import { DEFAULT_ITEM_FACE, ITEM_FACES, ITEM_MAX_UNITS, RACK_UNIT_STEP } from '../constants.ts';
import { detailsMatchType, extrasSchema, itemTypeSchema } from '../item-types.ts';
import { identifierSchema } from '../naming.ts';
import {
  dateSchema,
  idSchema,
  optionalNameSchema,
  shortTextSchema,
  sortOrderSchema,
  timestampSchema,
} from './common.ts';

// `details` is typed loosely here; the Worker and the form validate it with
// detailsSchemaFor(type) once the type is known.
const detailsSchema = z.record(z.string(), z.unknown());

/**
 * Where a device is in its life. 'installed' is the norm: it is what an item
 * gets when nothing is said, and the only status the lists stay quiet about.
 */
export const ITEM_STATUSES = ['planned', 'installed', 'spare', 'retired'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];
export const itemStatusSchema = z.enum(ITEM_STATUSES);
export const DEFAULT_ITEM_STATUS: ItemStatus = 'installed';
export const ITEM_STATUS_LABELS: Readonly<Record<ItemStatus, string>> = {
  planned: 'Planned',
  installed: 'Installed',
  spare: 'Spare',
  retired: 'Retired',
};

/** The chip beside an item's name: its status when it is not the norm, else nothing. */
export function itemStatusTag(item: { readonly status: ItemStatus }): string {
  return item.status === DEFAULT_ITEM_STATUS ? '' : ITEM_STATUS_LABELS[item.status];
}

/** A date in the item's life, or null when not recorded. */
const lifecycleDateSchema = z.nullable(dateSchema);

/** Which face of the rack a device sits on (ROADMAP A2). */
export const ITEM_FACE_LABELS: Readonly<Record<(typeof ITEM_FACES)[number], string>> = {
  front: 'Front',
  back: 'Back',
};
export const itemFaceSchema = z.enum(ITEM_FACES);
export type ItemFace = (typeof ITEM_FACES)[number];

/**
 * A mounted device's height in rack units (ROADMAP A12), or null when it was
 * never set. Half-U gear is allowed, so a height is a positive multiple of a
 * half unit — 0.5U, 1U, 1.5U, 2U … — from one half unit up to ITEM_MAX_UNITS.
 * A whole-U device (the norm before A12) is an integer, which is still a
 * multiple of a half unit, so every existing height reads unchanged. Presets
 * carry the same height, so shared/schemas/item-preset.ts reuses this.
 */
export const rackUnitsSchema = z.nullable(
  z.number().check(
    z.gte(RACK_UNIT_STEP, 'A height is at least half a unit'),
    z.lte(ITEM_MAX_UNITS),
    z.refine(
      (value: number) => Number.isInteger(value / RACK_UNIT_STEP),
      'A height steps by half a unit',
    ),
  ),
);

export const itemSchema = z.object({
  id: idSchema,
  roomId: z.nullable(idSchema),
  rackId: z.nullable(idSchema),
  /** The shelf or drawer this sits in, for rack items. */
  parentItemId: z.nullable(idSchema),
  /**
   * The device this hangs off, when it is a child on another device's port
   * (ROADMAP C10): an IP phone off a switch port, a PC behind that phone, a
   * module in a chassis slot. Independent of where the device is placed
   * (a room, a rack, or inventory), so a phone in a room can still hang off a
   * switch port in the rack; null when it hangs off nothing.
   */
  portParentItemId: z.nullable(idSchema),
  /** The port on `portParentItemId` this hangs off; null when none is set. */
  portParentPort: z.nullable(z.int().check(z.positive())),
  /** Height in rack units when mounted, in half-U steps, or null when never set. */
  units: rackUnitsSchema,
  /** Which rack face it sits on; front unless said (ROADMAP A2). */
  face: itemFaceSchema,
  /** Whether it occupies the full depth of the rack, taking both faces. */
  fullDepth: z.boolean(),
  type: itemTypeSchema,
  name: z.string(),
  /** Short path segment, distinct from the name; blank only in an unsynced or legacy cache. */
  identifier: z._default(z.string(), ''),
  make: z.string(),
  model: z.string(),
  serial: z.string(),
  ipAddress: z.string(),
  macAddress: z.string(),
  firmware: z.string(),
  locationNote: z.string(),
  status: itemStatusSchema,
  purchasedOn: lifecycleDateSchema,
  installedOn: lifecycleDateSchema,
  warrantyEnds: lifecycleDateSchema,
  supportEnds: lifecycleDateSchema,
  supplier: z.string(),
  orderRef: z.string(),
  /**
   * The rest of what an asset register carries (ROADMAP J1): the day the
   * device went into service and the day it came out, what it cost in whole
   * cents of the account's own currency, the purchase order it was bought
   * on, and the vendor as one of the client's contacts. The contact sits
   * beside `supplier`, which keeps its free text, so a device bought before
   * J1 — or from a supplier nobody has made a contact of — still reads.
   */
  operationStart: lifecycleDateSchema,
  decommissionedOn: lifecycleDateSchema,
  costCents: z.nullable(z.int().check(z.nonnegative())),
  poNumber: z.string(),
  supplierContactId: z.nullable(idSchema),
  /**
   * Where the device physically is while it waits to be installed (ROADMAP
   * J2): one of the account's stock places, or null when it is not in stock.
   * Independent of the container above — a device in the warehouse still
   * belongs to the property it is destined for — and cleared as soon as a
   * room or a rack is set, since by then it is on site. It is changed
   * through POST /api/items/:id/stock alone, so it is not one of the patch
   * fields below.
   */
  stockPlaceId: z.nullable(idSchema),
  details: detailsSchema,
  extras: extrasSchema,
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Item = z.infer<typeof itemSchema>;

// A patch is a partial of the plain fields: the input's defaults must not
// reset the height or holder of an item an edit does not mention.
const itemFields = {
  type: itemTypeSchema,
  name: optionalNameSchema,
  // Blank asks the server to fill it from the naming scheme (ROADMAP B5).
  identifier: z.optional(identifierSchema),
  make: shortTextSchema,
  model: shortTextSchema,
  serial: shortTextSchema,
  ipAddress: shortTextSchema,
  macAddress: shortTextSchema,
  firmware: shortTextSchema,
  locationNote: shortTextSchema,
  status: itemStatusSchema,
  purchasedOn: lifecycleDateSchema,
  installedOn: lifecycleDateSchema,
  warrantyEnds: lifecycleDateSchema,
  supportEnds: lifecycleDateSchema,
  supplier: shortTextSchema,
  orderRef: shortTextSchema,
  operationStart: lifecycleDateSchema,
  decommissionedOn: lifecycleDateSchema,
  costCents: z.nullable(z.int().check(z.nonnegative())),
  poNumber: shortTextSchema,
  supplierContactId: z.nullable(idSchema),
  details: detailsSchema,
  extras: extrasSchema,
  units: rackUnitsSchema,
  parentItemId: z.nullable(idSchema),
  portParentItemId: z.nullable(idSchema),
  portParentPort: z.nullable(z.int().check(z.positive())),
  face: itemFaceSchema,
  fullDepth: z.boolean(),
};

export const itemInputSchema = z.object({
  ...itemFields,
  // The lifecycle is optional on create: a device added on site is installed
  // today, with nothing known about when it was bought or how long it is covered.
  status: z._default(itemFields.status, DEFAULT_ITEM_STATUS),
  purchasedOn: z._default(itemFields.purchasedOn, null),
  installedOn: z._default(itemFields.installedOn, null),
  warrantyEnds: z._default(itemFields.warrantyEnds, null),
  supportEnds: z._default(itemFields.supportEnds, null),
  supplier: z._default(itemFields.supplier, ''),
  orderRef: z._default(itemFields.orderRef, ''),
  // The purchase details (ROADMAP J1) are optional rather than defaulted:
  // every caller that builds an item — the import, the duplicate, the plan
  // editor's drop, the form — would otherwise have to name five fields it
  // knows nothing about. Left out, each is stored at its blank.
  operationStart: z.optional(itemFields.operationStart),
  decommissionedOn: z.optional(itemFields.decommissionedOn),
  costCents: z.optional(itemFields.costCents),
  poNumber: z.optional(itemFields.poNumber),
  supplierContactId: z.optional(itemFields.supplierContactId),
  units: z._default(itemFields.units, null),
  parentItemId: z._default(itemFields.parentItemId, null),
  portParentItemId: z._default(itemFields.portParentItemId, null),
  portParentPort: z._default(itemFields.portParentPort, null),
  face: z._default(itemFields.face, DEFAULT_ITEM_FACE),
  fullDepth: z._default(itemFields.fullDepth, false),
});
export type ItemInput = z.infer<typeof itemInputSchema>;

/**
 * The input with `details` checked against its type's own schema, so the
 * form reports a bad VLAN tag or port row on the field itself instead of
 * after the round trip. The Worker runs the same per-type check.
 */
export const itemFormSchema = itemInputSchema.check(detailsMatchType<ItemInput>());

export const itemPatchSchema = z.partial(z.object(itemFields));
export type ItemPatch = z.infer<typeof itemPatchSchema>;

/** The purchase fields of a stored item (ROADMAP J1), which an input may leave out. */
export type ItemPurchase = Pick<
  Item,
  'operationStart' | 'decommissionedOn' | 'costCents' | 'poNumber' | 'supplierContactId'
>;

/**
 * The purchase details an input carries, each at its blank where the caller
 * left it out. They are optional on the input so the import, the duplicate,
 * and the plan editor's drop need not name five fields they know nothing
 * about; a stored item always has all five, and this is the one place that
 * turns the one shape into the other.
 */
export function itemPurchase(input: ItemInput): ItemPurchase {
  return {
    operationStart: input.operationStart ?? null,
    decommissionedOn: input.decommissionedOn ?? null,
    costCents: input.costCents ?? null,
    poNumber: input.poNumber ?? '',
    supplierContactId: input.supplierContactId ?? null,
  };
}

/** Body of POST /api/items/:id/move — exactly one destination. */
/** Where to move an item; `resetPorts` clears its port labels on the way. */
const moveOptions = { resetPorts: z.optional(z.boolean()) };
export const itemMoveSchema = z.union([
  z.object({ roomId: idSchema, ...moveOptions }),
  z.object({
    rackId: idSchema,
    /** A shelf or drawer in that rack to sit inside. */
    parentItemId: z.optional(z.nullable(idSchema)),
    ...moveOptions,
  }),
]);
export type ItemMove = z.infer<typeof itemMoveSchema>;
